import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../api/axiosInstance';
import Button from '../../ui/Button';
import Card, { CardHeader } from '../../ui/Card';
import { StatusBadge } from '../../ui/Table';
import DnsProviderDetection from './DnsProviderDetection';
import DnsManagement from './DnsManagement';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  ClockIcon,
  CogIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import logger from '../../../utils/logger';

const ModernDomainVerification = ({ domain, onBack, onVerified, showNotification }) => {
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationData, setVerificationData] = useState(null);
  const [error, setError] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [autoCheckEnabled, setAutoCheckEnabled] = useState(false);
  const [checkInterval, setCheckInterval] = useState(null);
  const [providerInfo, setProviderInfo] = useState(null);
  const [automating, setAutomating] = useState(false);
  const [showDnsManagement, setShowDnsManagement] = useState(false);

  // Set up verification data
  const setupVerificationData = useCallback(() => {
    if (!domain) return;

    try {
      setLoading(true);
      
      let verificationValue = "";
      
      if (domain.dnsRecords && Array.isArray(domain.dnsRecords)) {
        const verificationRecord = domain.dnsRecords.find(
          record => record.type === 'TXT' && record.value && record.value.includes('aiinak-verify=')
        );
        
        if (verificationRecord) {
          verificationValue = verificationRecord.value;
        }
      }
      
      if (!verificationValue && domain.verificationToken) {
        verificationValue = `aiinak-verify=${domain.verificationToken}`;
      } else if (!verificationValue) {
        verificationValue = `aiinak-verify=${domain.id.substring(0, 32)}`;
      }
      
      setVerificationData({
        type: 'TXT',
        host: '@',
        value: verificationValue,
        isVerified: domain.isVerified || false
      });
      
    } catch (err) {
      logger.error('Error setting up verification data:', err);
      setVerificationData({
        type: 'TXT',
        host: '@',
        value: `aiinak-verify=${domain.id.substring(0, 32)}`,
        isVerified: domain.isVerified || false
      });
    } finally {
      setLoading(false);
    }
  }, [domain?.id, domain?.dnsRecords, domain?.verificationToken, domain?.isVerified]);

  useEffect(() => {
    if (domain?.id) {
      setupVerificationData();
    }
  }, [domain?.id]); // Only run when domain ID changes

  // Auto-check functionality
  useEffect(() => {
    if (autoCheckEnabled && !domain?.isVerified) {
      const interval = setInterval(() => {
        handleVerifyDomain(true);
      }, 30000); // Check every 30 seconds
      
      setCheckInterval(interval);
      
      return () => clearInterval(interval);
    } else if (checkInterval) {
      clearInterval(checkInterval);
      setCheckInterval(null);
    }
  }, [autoCheckEnabled, domain?.isVerified]);

  const handleVerifyDomain = async (isAutoCheck = false) => {
    try {
      setVerifying(true);
      setError(null);
      
      const response = await axiosInstance.post('/api/business/domains/verify', {
        domainId: domain.id
      });
      
      if (response.data.success) {
        if (response.data.domain && response.data.domain.isVerified) {
          showNotification && showNotification('Domain verified successfully!', 'success');
          setAutoCheckEnabled(false);
          
          if (onVerified) {
            onVerified(response.data.domain);
          }
        } else if (response.data.results) {
          const verifiedTxtRecord = response.data.results.find(
            result => result.type === 'TXT' && result.verified && result.value.includes('aiinak-verify=')
          );
          
          if (verifiedTxtRecord) {
            showNotification && showNotification('Domain verified successfully!', 'success');
            setAutoCheckEnabled(false);
            if (onVerified) {
              onVerified({
                ...domain,
                isVerified: true,
                status: 'active'
              });
            }
          } else {
            setValidationResults({
              verified: false,
              results: response.data.results
            });
            
            if (!isAutoCheck) {
              showNotification && showNotification('Domain verification failed. DNS records not found.', 'error');
            }
          }
        }
      }
    } catch (err) {
      logger.error('Error verifying domain:', err);
      setError(err.message || 'Verification failed. Please check your DNS settings.');
      if (!isAutoCheck) {
        showNotification && showNotification('Domain verification failed', 'error');
      }
    } finally {
      setVerifying(false);
    }
  };

  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    showNotification && showNotification('Copied to clipboard', 'success');
    
    setTimeout(() => {
      setCopiedField(null);
    }, 2000);
  };

  const handleProviderDetected = (detectedProvider) => {
    setProviderInfo(detectedProvider);
  };

  const handleAutomationClick = async (provider) => {
    if (provider.provider === 'cloudflare') {
      // Cloudflare automation is handled by the DnsProviderDetection component
      return;
    }
    
    // Handle other provider automation flows here
    setAutomating(true);
    try {
      const response = await axiosInstance.post(
        `/api/business/domains/${domain.id}/auto-create-records`
      );
      
      if (response.data.success) {
        showNotification && showNotification('DNS records created automatically!', 'success');
        // Refresh verification data
        setupVerificationData();
      } else {
        throw new Error(response.data.message || 'Automation failed');
      }
    } catch (err) {
      logger.error('DNS automation error:', err);
      showNotification && showNotification('Failed to create DNS records automatically', 'error');
    } finally {
      setAutomating(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-2 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading verification details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-[#111111]">
      {/* Header */}
      <div className="flex-shrink-0 bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                icon={<ArrowLeftIcon />}
              >
                Back
              </Button>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                  <ShieldCheckIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Domain Verification</h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{domain?.domainName}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <StatusBadge 
                status={domain?.isVerified ? 'Verified' : 'Pending'} 
                color={domain?.isVerified ? 'success' : 'warning'}
              />
              
              {!domain?.isVerified && (
                <div className="flex items-center space-x-2">
                  <label className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <input
                      type="checkbox"
                      checked={autoCheckEnabled}
                      onChange={(e) => setAutoCheckEnabled(e.target.checked)}
                      className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 mr-2"
                    />
                    Auto-check
                  </label>
                </div>
              )}
              
              {/* A verified domain must not be urged to verify again. Re-checking
                  stays available (DNS can change) but as a quiet secondary
                  action, not a primary call to action beside a Verified badge. */}
              <Button
                variant={domain?.isVerified ? 'secondary' : 'primary'}
                size="sm"
                onClick={() => handleVerifyDomain(false)}
                loading={verifying}
                icon={verifying ? <ArrowPathIcon className="animate-spin" /> : <CheckCircleIcon />}
                disabled={verifying}
              >
                {verifying
                  ? (domain?.isVerified ? 'Re-checking...' : 'Verifying...')
                  : (domain?.isVerified ? 'Re-check DNS' : 'Verify Now')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-5xl mx-auto space-y-6">
          
          {/* Status Alert */}
          {domain?.isVerified ? null : (
            <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30">
              <div className="flex items-center space-x-3">
                <ExclamationTriangleIcon className="w-6 h-6 text-amber-500" />
                <div>
                  <h3 className="font-medium text-amber-900 dark:text-amber-300">
                    {validationResults ? 'Verification Failed' : 'Verification Required'}
                  </h3>
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    {validationResults
                      ? 'DNS records not found. Please check your configuration below.'
                      : 'Add the DNS record below to verify domain ownership.'}
                  </p>
                </div>
              </div>
            </Card>
          )}

          {error && (
            <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30">
              <div className="flex items-center space-x-3">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-500" />
                <div>
                  <h3 className="font-medium text-red-900 dark:text-red-300">Verification Error</h3>
                  <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                </div>
              </div>
            </Card>
          )}

          {/* DNS Provider Detection */}
          <DnsProviderDetection
            domain={domain}
            onProviderDetected={handleProviderDetected}
            onAutomationClick={handleAutomationClick}
            showNotification={showNotification}
          />

          {/* DNS Management Section */}
          {providerInfo && providerInfo.canAutomate && providerInfo.connectedAt && (
            <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <CogIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  <div>
                    <h3 className="font-medium text-blue-900 dark:text-blue-300">DNS Management Available</h3>
                    <p className="text-sm text-blue-700 dark:text-blue-400">
                      Manage DNS records directly through {providerInfo.name}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDnsManagement(true)}
                  icon={<CogIcon />}
                >
                  Manage DNS
                </Button>
              </div>
            </Card>
          )}

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-2 gap-6">
            
            {/* DNS Record Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <GlobeAltIcon className="w-5 h-5 text-orange-600" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">DNS Verification Record</h3>
                </div>
              </CardHeader>
              
              <div className="space-y-4">
                {/* Record Fields */}
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Record Type</label>
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => copyToClipboard(verificationData?.type || 'TXT', 'type')}
                        icon={<ClipboardDocumentIcon />}
                        className={copiedField === 'type' ? 'text-green-600' : ''}
                      >
                        {copiedField === 'type' ? 'Copied!' : 'Copy'}
                      </Button>
                    </div>
                    <div className="bg-gray-50 dark:bg-[#222222] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 font-mono text-sm text-gray-900 dark:text-white">
                      {verificationData?.type || 'TXT'}
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Host/Name</label>
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => copyToClipboard(verificationData?.host || '@', 'host')}
                        icon={<ClipboardDocumentIcon />}
                        className={copiedField === 'host' ? 'text-green-600' : ''}
                      >
                        {copiedField === 'host' ? 'Copied!' : 'Copy'}
                      </Button>
                    </div>
                    <div className="bg-gray-50 dark:bg-[#222222] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 font-mono text-sm text-gray-900 dark:text-white">
                      {verificationData?.host || '@'}
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Value</label>
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => copyToClipboard(verificationData?.value || '', 'value')}
                        icon={<ClipboardDocumentIcon />}
                        className={copiedField === 'value' ? 'text-green-600' : ''}
                      >
                        {copiedField === 'value' ? 'Copied!' : 'Copy'}
                      </Button>
                    </div>
                    <div className="bg-gray-50 dark:bg-[#222222] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 font-mono text-sm text-gray-900 dark:text-white break-all">
                      {verificationData?.value || 'Loading...'}
                    </div>
                  </div>
                </div>
                
                {/* Quick Actions */}
                <div className="flex space-x-2 pt-2 border-t">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => copyToClipboard(
                      `Host: ${verificationData?.host}\nType: ${verificationData?.type}\nValue: ${verificationData?.value}`,
                      'all'
                    )}
                    className={`flex-1 ${copiedField === 'all' ? 'text-green-600' : ''}`}
                  >
                    {copiedField === 'all' ? 'All Fields Copied!' : 'Copy All Fields'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={setupVerificationData}
                    icon={<ArrowPathIcon />}
                  >
                    Refresh
                  </Button>
                </div>
              </div>
            </Card>

            {/* Instructions Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <InformationCircleIcon className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Setup Instructions</h3>
                </div>
              </CardHeader>
              
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-semibold text-orange-600">1</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Access DNS Settings</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Log into your domain registrar's control panel (GoDaddy, Namecheap, etc.)</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-semibold text-orange-600">2</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Add DNS Record</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Create a new TXT record using the exact values shown on the left</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-semibold text-orange-600">3</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Wait for Propagation</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">DNS changes can take 5-30 minutes to propagate globally</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-semibold text-orange-600">4</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Verify Domain</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Click "Verify Now" or enable auto-check for automatic verification</p>
                    </div>
                  </div>
                </div>
                
                {/* Pro Tips */}
                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-4">
                  <div className="flex items-start space-x-2">
                    <InformationCircleIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-blue-900 dark:text-blue-300">Pro Tips</h4>
                      <ul className="text-sm text-blue-800 dark:text-blue-400 mt-1 space-y-1">
                        <li>• Use "@" or leave host field empty for root domain</li>
                        <li>• Enable auto-check to monitor verification status</li>
                        <li>• Contact support if verification fails after 24 hours</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Additional Email Records - Compact View */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <GlobeAltIcon className="w-4 h-4 text-orange-600" />
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">Email DNS Records</h3>
                  <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-[#222222] px-2 py-1 rounded">Required for email delivery</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const allRecords = [
                      'MX Record:\n@    IN  MX  10  mail.aiinak.com',
                      'SPF Record (choose one):\n@    IN  TXT  "v=spf1 include:aiinak.com ip4:172.105.28.214 ~all"\n@    IN  TXT  "v=spf1 ip4:172.105.28.214 ~all"',
                      'DKIM Record:\nmail._domainkey    IN  TXT  "v=DKIM1; k=rsa; p=MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAzA4mfeebldOQYLbd0xbDBX77wqM6N04a6X6BMpJ4m5fp1ptm5kIX9n1WqyDTqdyN1K8qrb3daudgmi8p0hwMO+2/aGP7e/LpoNc4ujWRcEgVehUcDlKAKsDRFCxg80//wVATDblm6isL0Xo1XmWKI5hJ0dpSnV8XNe7adN7TKoPao1sZnhemXuoDjzpA1XSAOjna20vr1HOu+y8O3Id9+ijv4gWotavHrEUAMUu0RZ6m2RUnwnc8IhqQnEtf1XIGb/XZScmzoPEl1RZN0ySST dZwry5dypIYp2LpBZVJSIiiOEvDc/gss5jW19Ek1CKh8XblCSkRi2zp8ddyEhUMaQIDAQAB"',
                      'DMARC Record:\n_dmarc    IN  TXT  "v=DMARC1; p=none; rua=mailto:dmarc@aiinak.com"'
                    ].join('\n\n');
                    copyToClipboard(allRecords, 'all-email-records');
                  }}
                  icon={<ClipboardDocumentIcon />}
                  className={copiedField === 'all-email-records' ? 'text-green-600' : ''}
                >
                  {copiedField === 'all-email-records' ? 'All Copied!' : 'Copy All'}
                </Button>
              </div>
            </CardHeader>
            
            <div className="space-y-3">
              {/* Compact Records Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-800">
                      <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">Type</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">Host</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">Value</th>
                      <th className="text-center py-2 px-3 font-medium text-gray-700 dark:text-gray-300 w-16">Copy</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-100">
                      <td className="py-2 px-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">MX</span>
                      </td>
                      <td className="py-2 px-3 font-mono text-gray-600 dark:text-gray-400">@</td>
                      <td className="py-2 px-3 font-mono text-gray-900 dark:text-white">10 mail.aiinak.com</td>
                      <td className="py-2 px-3 text-center">
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => copyToClipboard('@\nMX\n10\nmail.aiinak.com', 'mx-record')}
                          icon={<ClipboardDocumentIcon />}
                          className={`${copiedField === 'mx-record' ? 'text-green-600' : 'text-gray-400 hover:text-gray-600'} p-1`}
                        />
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2 px-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">SPF</span>
                      </td>
                      <td className="py-2 px-3 font-mono text-gray-600 dark:text-gray-400">@</td>
                      <td className="py-2 px-3 font-mono text-gray-900 dark:text-white break-all">v=spf1 include:aiinak.com ip4:172.105.28.214 ~all</td>
                      <td className="py-2 px-3 text-center">
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => copyToClipboard('@\nTXT\nv=spf1 include:aiinak.com ip4:172.105.28.214 ~all', 'spf-record')}
                          icon={<ClipboardDocumentIcon />}
                          className={`${copiedField === 'spf-record' ? 'text-green-600' : 'text-gray-400 hover:text-gray-600'} p-1`}
                        />
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2 px-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">DKIM</span>
                      </td>
                      <td className="py-2 px-3 font-mono text-gray-600 dark:text-gray-400">mail._domainkey</td>
                      <td className="py-2 px-3 font-mono text-gray-900 dark:text-white truncate max-w-xs">v=DKIM1; k=rsa; p=MII...</td>
                      <td className="py-2 px-3 text-center">
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => copyToClipboard('mail._domainkey\nTXT\nv=DKIM1; k=rsa; p=MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAzA4mfeebldOQYLbd0xbDBX77wqM6N04a6X6BMpJ4m5fp1ptm5kIX9n1WqyDTqdyN1K8qrb3daudgmi8p0hwMO+2/aGP7e/LpoNc4ujWRcEgVehUcDlKAKsDRFCxg80//wVATDblm6isL0Xo1XmWKI5hJ0dpSnV8XNe7adN7TKoPao1sZnhemXuoDjzpA1XSAOjna20vr1HOu+y8O3Id9+ijv4gWotavHrEUAMUu0RZ6m2RUnwnc8IhqQnEtf1XIGb/XZScmzoPEl1RZN0ySST dZwry5dypIYp2LpBZVJSIiiOEvDc/gss5jW19Ek1CKh8XblCSkRi2zp8ddyEhUMaQIDAQAB', 'dkim-record')}
                          icon={<ClipboardDocumentIcon />}
                          className={`${copiedField === 'dkim-record' ? 'text-green-600' : 'text-gray-400 hover:text-gray-600'} p-1`}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 px-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">DMARC</span>
                      </td>
                      <td className="py-2 px-3 font-mono text-gray-600 dark:text-gray-400">_dmarc</td>
                      <td className="py-2 px-3 font-mono text-gray-900 dark:text-white">v=DMARC1; p=none; rua=mailto:dmarc@aiinak.com</td>
                      <td className="py-2 px-3 text-center">
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => copyToClipboard('_dmarc\nTXT\nv=DMARC1; p=none; rua=mailto:dmarc@aiinak.com', 'dmarc-record')}
                          icon={<ClipboardDocumentIcon />}
                          className={`${copiedField === 'dmarc-record' ? 'text-green-600' : 'text-gray-400 hover:text-gray-600'} p-1`}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              {/* Quick Tips */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <InformationCircleIcon className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-900">Quick Setup Tips:</p>
                    <p className="text-amber-800 mt-1">• Choose only ONE SPF record • DKIM and DMARC are optional but recommended • Replace @ with your domain if required by your DNS provider</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

        </div>
      </div>

      {/* DNS Management Modal */}
      {showDnsManagement && (
        <DnsManagement
          open={showDnsManagement}
          onClose={() => setShowDnsManagement(false)}
          domainName={domain?.domainName}
          dnsRecords={domain?.dnsRecords}
          verificationToken={domain?.verificationToken}
          domainId={domain?.id}
          isNewDomain={!domain?.isVerified}
          onVerificationComplete={() => {
            setupVerificationData();
            setShowDnsManagement(false);
          }}
        />
      )}
    </div>
  );
};

export default ModernDomainVerification;